
import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    IconButton,
    InputAdornment,
    LinearProgress,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';

const SENHA_MINIMA = 6;

const emailValido = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Pontua de 0 a 4: tamanho, letras maiúsculas e minúsculas, números e símbolos
const forcaSenha = (senha) => {
    if (!senha) return 0;
    let pontos = 0;
    if (senha.length >= SENHA_MINIMA) pontos++;
    if (senha.length >= 10) pontos++;
    if (/[a-z]/.test(senha) && /[A-Z]/.test(senha)) pontos++;
    if (/\d/.test(senha) && /[^A-Za-z0-9]/.test(senha)) pontos++;
    return senha.length < SENHA_MINIMA ? Math.min(pontos, 1) : pontos;
};

const niveisForca = [
    { label: 'Muito fraca', cor: '#dc2626' },
    { label: 'Fraca', cor: '#dc2626' },
    { label: 'Razoável', cor: '#d97706' },
    { label: 'Boa', cor: '#16a34a' },
    { label: 'Forte', cor: '#15803d' },
];

const labelSx = { mb: 0.75, fontSize: 12, fontWeight: 600, color: '#374151' };

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        fontSize: 14,
        backgroundColor: '#fff',
        '&.Mui-focused fieldset': { borderColor: '#0b243d' },
    },
    '& .MuiFormHelperText-root': { mx: 0, fontSize: 12 },
};

const iconeInput = (Icone) => (
    <InputAdornment position="start">
        <Icone sx={{ fontSize: 18, color: '#9ca3af' }} />
    </InputAdornment>
);

// Cadastra uma nova secretária ou, quando recebe `secretaria`, edita a existente
const ModalSecretaria = (props) => {
    const theme = useTheme();
    const telaPequena = useMediaQuery(theme.breakpoints.down('sm'));

    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [confirmacao, setConfirmacao] = useState('');
    const [mostrarSenha, setMostrarSenha] = useState(false);
    const [tentouEnviar, setTentouEnviar] = useState(false);
    const [erroApi, setErroApi] = useState('');
    const [loading, setLoading] = useState(false);

    const editando = Boolean(props.secretaria);

    useEffect(() => {
        if (props.open) {
            setNome(props.secretaria?.usuario.Nome ?? '');
            setEmail(props.secretaria?.usuario.Email ?? '');
            setSenha('');
            setConfirmacao('');
            setMostrarSenha(false);
            setTentouEnviar(false);
            setErroApi('');
        }
    }, [props.open, props.secretaria]);

    // Na edição, a senha é opcional: só é validada se o usuário digitar uma nova
    const validar = () => {
        const erros = {};
        if (!nome.trim()) erros.nome = 'Informe o nome da secretária.';
        else if (nome.trim().length < 3) erros.nome = 'O nome deve ter pelo menos 3 caracteres.';

        if (!email.trim()) erros.email = 'Informe o e-mail de acesso.';
        else if (!emailValido(email.trim())) erros.email = 'Informe um e-mail válido.';

        if (!editando || senha || confirmacao) {
            if (!senha) erros.senha = 'Informe uma senha.';
            else if (senha.length < SENHA_MINIMA) erros.senha = `A senha deve ter pelo menos ${SENHA_MINIMA} caracteres.`;

            if (senha && confirmacao !== senha) erros.confirmacao = 'As senhas não coincidem.';
        }
        return erros;
    };

    // Os erros só aparecem depois da primeira tentativa de envio e somem conforme o usuário corrige
    const erros = tentouEnviar ? validar() : {};

    const handleClose = () => {
        if (loading) return;
        props.onClose();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setTentouEnviar(true);
        setErroApi('');

        if (Object.keys(validar()).length > 0) return;

        setLoading(true);

        const dados = { nome: nome.trim(), email: email.trim().toLowerCase() };
        if (senha) dados.senha = senha;

        const config = {
            headers: {
                Authorization: localStorage.getItem('token'),
                'Content-Type': 'application/json'
            }
        };

        const mensagemPadrao = `Erro ao ${editando ? 'editar' : 'cadastrar'} secretária. Tente novamente.`;

        try {
            const response = editando
                ? await axios.put(`${process.env.REACT_APP_API_URL}/editarSecretaria/${props.secretaria.idSecretaria}`, dados, config)
                : await axios.post(`${process.env.REACT_APP_API_URL}/cadastrarSecretaria`, dados, config);

            if (response.data.status === 200) {
                props.onSuccess(editando ? 'Secretária atualizada com sucesso!' : 'Secretária cadastrada com sucesso!');
            } else {
                setErroApi(response.data.message || mensagemPadrao);
            }
        } catch (error) {
            setErroApi(error.response?.data?.message || mensagemPadrao);
        } finally {
            setLoading(false);
        }
    };

    const forca = forcaSenha(senha);
    const nivel = niveisForca[forca];
    const exibirConfirmacao = !editando || senha.length > 0;

    const botaoVisibilidade = (
        <InputAdornment position="end">
            <IconButton
                size="small"
                aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => setMostrarSenha((valor) => !valor)}
                edge="end"
                tabIndex={-1}
            >
                {mostrarSenha ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            fullScreen={telaPequena}
            aria-labelledby="modal-secretaria-title"
            aria-describedby="modal-secretaria-description"
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: handleSubmit,
                    noValidate: true,
                    sx: { borderRadius: telaPequena ? 0 : '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' },
                }
            }}
        >
            {/* Cabeçalho */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 3, pt: 3, pb: 2, borderBottom: '1px solid #f3f4f6' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: '#0b243d', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {editando
                        ? <ManageAccountsOutlinedIcon sx={{ fontSize: 22, color: '#ffc845' }} />
                        : <PersonAddAltOutlinedIcon sx={{ fontSize: 22, color: '#ffc845' }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography id="modal-secretaria-title" sx={{ fontSize: 18, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                        {editando ? 'Editar secretária' : 'Nova secretária'}
                    </Typography>
                    <Typography id="modal-secretaria-description" sx={{ mt: 0.25, fontSize: 13, color: '#6b7280' }}>
                        {editando
                            ? 'Atualize os dados de acesso ao painel.'
                            : 'Crie um acesso para ela auxiliar na rotina do consultório.'}
                    </Typography>
                </Box>
                <IconButton size="small" aria-label="Fechar" onClick={handleClose} disabled={loading} sx={{ mt: -0.5, mr: -1, color: '#6b7280' }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {/* Formulário */}
            <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2, flex: telaPequena ? 1 : 'initial', overflowY: 'auto' }}>
                {erroApi && (
                    <Alert severity="error" onClose={() => setErroApi('')} sx={{ borderRadius: '8px', fontSize: 13 }}>
                        {erroApi}
                    </Alert>
                )}

                <Box>
                    <Typography component="label" htmlFor="secretaria-nome" sx={labelSx}>Nome completo</Typography>
                    <TextField
                        id="secretaria-nome"
                        placeholder="Ex.: Ana Paula Souza"
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                        autoComplete="off"
                        disabled={loading}
                        error={Boolean(erros.nome)}
                        helperText={erros.nome}
                        sx={inputSx}
                        slotProps={{ input: { startAdornment: iconeInput(PersonOutlineIcon) } }}
                    />
                </Box>

                <Box>
                    <Typography component="label" htmlFor="secretaria-email" sx={labelSx}>E-mail de acesso</Typography>
                    <TextField
                        id="secretaria-email"
                        type="email"
                        placeholder="secretaria@consultorio.com.br"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        size="small"
                        fullWidth
                        autoComplete="off"
                        disabled={loading}
                        error={Boolean(erros.email)}
                        helperText={erros.email || 'Será usado como login no painel.'}
                        sx={inputSx}
                        slotProps={{ input: { startAdornment: iconeInput(MailOutlineIcon) } }}
                    />
                </Box>

                <Box>
                    <Typography component="label" htmlFor="secretaria-senha" sx={labelSx}>
                        {editando ? 'Nova senha' : 'Senha'}
                        {editando && <Box component="span" sx={{ ml: 0.5, fontWeight: 400, color: '#9ca3af' }}>(opcional)</Box>}
                    </Typography>
                    <TextField
                        id="secretaria-senha"
                        type={mostrarSenha ? 'text' : 'password'}
                        placeholder={editando ? 'Deixe em branco para manter a atual' : `Mínimo de ${SENHA_MINIMA} caracteres`}
                        value={senha}
                        onChange={(e) => setSenha(e.target.value)}
                        size="small"
                        fullWidth
                        autoComplete="new-password"
                        disabled={loading}
                        error={Boolean(erros.senha)}
                        helperText={erros.senha}
                        sx={inputSx}
                        slotProps={{ input: { startAdornment: iconeInput(LockOutlinedIcon), endAdornment: botaoVisibilidade } }}
                    />
                    {senha && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={(forca / 4) * 100}
                                sx={{ flex: 1, height: 5, borderRadius: 3, backgroundColor: '#e5e7eb', '& .MuiLinearProgress-bar': { backgroundColor: nivel.cor, borderRadius: 3 } }}
                            />
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: nivel.cor, minWidth: 70, textAlign: 'right' }}>
                                {nivel.label}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {exibirConfirmacao && (
                    <Box>
                        <Typography component="label" htmlFor="secretaria-confirmacao" sx={labelSx}>Confirmar senha</Typography>
                        <TextField
                            id="secretaria-confirmacao"
                            type={mostrarSenha ? 'text' : 'password'}
                            placeholder="Repita a senha"
                            value={confirmacao}
                            onChange={(e) => setConfirmacao(e.target.value)}
                            size="small"
                            fullWidth
                            autoComplete="new-password"
                            disabled={loading}
                            error={Boolean(erros.confirmacao)}
                            helperText={erros.confirmacao}
                            sx={inputSx}
                            slotProps={{ input: { startAdornment: iconeInput(LockResetOutlinedIcon) } }}
                        />
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', p: 1.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#6b7280', mt: '1px' }} />
                    <Typography sx={{ fontSize: 12, color: '#4b5563' }}>
                        {editando
                            ? 'Se a senha for alterada, informe a nova senha à secretária.'
                            : 'Envie o e-mail e a senha para a secretária. Ela poderá acessar o painel da sua empresa com esses dados.'}
                    </Typography>
                </Box>
            </Box>

            {/* Ações */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: 1, px: 3, py: 2, borderTop: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#374151', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', px: 2, '&:hover': { backgroundColor: '#f3f4f6' } }}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    loading={loading}
                    variant="contained"
                    startIcon={editando ? <ManageAccountsOutlinedIcon /> : <PersonAddAltOutlinedIcon />}
                    sx={{ textTransform: 'none', fontSize: 13, fontWeight: 700, color: '#111827', backgroundColor: '#ffc845', borderRadius: '8px', boxShadow: 'none', px: 2.5, '&:hover': { backgroundColor: '#f5b82e', boxShadow: 'none' } }}
                >
                    {editando ? 'Salvar alterações' : 'Cadastrar secretária'}
                </Button>
            </Box>
        </Dialog>
    );
}

export default ModalSecretaria;
